import { AxiosResponse, CancelToken } from 'axios';
import axios from '../helpers/axios';
import { logError } from '../services';
import {
  IBuildRes,
  IBuildStatus,
  IPeopleSimple,
  IResponse,
  IBuildOptionReportRes,
  IFieldWalkthrough,
  IBuildPost,
  IBuildTenDayDetails,
  IBuildTenDayLockoutDatePut,
  ISpecDetailResponse,
  ISpecCategory,
  ICategory,
  IColorSchemeOption,
  IBuildContingencyPost,
  IUpdateBuild,
  IUpdateOptions,
  IContractAdjustment,
  IBuild,
  IBuildCeremonies,
  IDropdownResponse,
  ILenderPayload,
  BuildStatusValue
} from '../models';
import fileDownload from 'js-file-download';
import qs from 'querystring';
import { base64StringToBlob } from '../helpers';
import contentDisposition from 'content-disposition';

export const getBuilds = async (filters?: {
  neighborhoodId?: number | string;
  lotNumber?: number | string;
  phase?: string;
  isActive?: boolean;
  isSpec?: boolean;
  personId?: number | string;
  agentId?: number | string;
  status?: string;
  planId?: number | string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number | string;
  planElevationId?: number | string;
  isForPlanFinder?: boolean;
}): Promise<IBuildRes> => {
  try {
    const { data } = await axios.get(`/api/builds`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuilds failed');
    return Promise.reject(error);
  }
};

export const getBuildById = async (id: string | number): Promise<ISpecDetailResponse> => {
  try {
    const response = await axios.get(`/api/builds/${id}`);
    return response.data;
  } catch (error) {
    logError(error, 'getBuildById failed');
    return Promise.reject(error);
  }
};

export const deleteBuild = async (buildId: number, jobType: string): Promise<any> => {
  try {
    const { data } = await axios.delete(`/api/builds/${buildId}`, {
      params: {
        jobType
      }
    });
    return data;
  } catch (error) {
    logError(error, 'deleteBuild failed');
    return Promise.reject(error);
  }
};

export const resetBuild = async (buildId: number): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/builds/${buildId}/reset`);
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          buildId
        }
      },
      'resetBuild failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const resetSpec = async (buildId: number): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/builds/${buildId}/reset-spec`);
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          buildId
        }
      },
      'resetSpec failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getBuildStatuses = async (): Promise<IBuildStatus[]> => {
  try {
    const { data } = await axios.get('/api/builds/statuses');
    return data;
  } catch (error) {
    logError(
      {
        error
      },
      'getBuildStatuses failed'
    );
    return Promise.reject(error);
  }
};

export const getBuildFieldWalkthroughs = async (
  buildId: string | number,
  filters?: {
    isComplete?: boolean;
  }
): Promise<IFieldWalkthrough[]> => {
  try {
    const { data } = await axios.get(`/api/builds/${buildId}/field-walkthroughs`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(
      {
        error
      },
      'getBuildFieldWalkthroughs failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getAgentsSimple = async (): Promise<IResponse<IPeopleSimple[]>> => {
  try {
    const { data } = await axios.get('/api/people/lookup-simple', {
      params: { personType: 'InternalAgent', isActive: true }
    });
    return data;
  } catch (error) {
    logError(
      {
        error
      },
      'getAgents failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const postAddendum = async (buildId: number): Promise<any> => {
  try {
    await axios.post(`/api/builds/${buildId}/addendum-f`).then(result => {
      fileDownload(base64StringToBlob(result.data.content), result.data.fileName);
    });
  } catch (error) {
    logError(
      {
        error,
        payload: {
          buildId
        }
      },
      'postAddendum failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const postTestContract = async (buildId: number): Promise<any> => {
  try {
    await axios.post(`/api/builds/${buildId}/test-contract`).then(result => {
      fileDownload(base64StringToBlob(result.data.content), result.data.fileName);
    });
  } catch (error) {
    logError(
      {
        error,
        payload: {
          buildId
        }
      },
      'postTestContract failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const convertBuildToSpec = async (buildId: number): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/builds/${buildId}/convert-to-spec`);
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          buildId
        }
      },
      'convertBuildToSpec failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getBuildOptionReports = async (filters?: {
  neighborhoodId?: number | string;
  lotNumber?: number | string;
  isActive?: boolean;
  hasSpecs?: boolean;
  hasActiveJobs?: boolean;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
}): Promise<IBuildOptionReportRes> => {
  try {
    const { data } = await axios.get(`/api/specs/selected-options-report`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuildOptionReports failed');
    return Promise.reject(error);
  }
};

export const regenerateTenDayAcceptanceDocs = async (
  buildId: number,
  downloadZip: boolean,
  cancelToken?: CancelToken
): Promise<any> => {
  try {
    await axios
      .post(`/api/builds/${buildId}/ten-day-acceptance`, null, {
        cancelToken,
        responseType: 'blob'
      })
      .then(result => {
        if (downloadZip) {
          const parsedContentDisposition = contentDisposition.parse(
            result.headers['content-disposition']
          );
          const { filename: fileName } = parsedContentDisposition.parameters;
          fileDownload(result.data, fileName);
        }
      });
  } catch (error) {
    logError(
      {
        error,
        payload: {
          buildId
        }
      },
      'regenerateTenDayAcceptanceDocs failed'
    );
    return Promise.reject(error);
  }
};

export const regenerateLandscapeDocument = async (
  buildId: number | string,
  cancelToken?: CancelToken
): Promise<any> => {
  try {
    await axios.post(`/api/builds/${buildId}/regenerate-landscape`, null, {
      cancelToken
    });
  } catch (error) {
    logError(
      {
        error,
        payload: {
          buildId
        }
      },
      'regenerateLandscapeDocument failed'
    );
    return Promise.reject(error.response.data);
  }
};

export const createBuild = async (payload: IBuildPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/builds`, payload);
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: payload
      },
      'createBuild failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const createTestBuild = async (payload: IBuildPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/builds/test-build`, payload);
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: payload
      },
      'createTestBuild failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getBuildTenDayDetails = async (
  buildId: string | number
): Promise<IBuildTenDayDetails> => {
  try {
    const { data } = await axios.get(`/api/builds/${buildId}/ten-day`);
    return data;
  } catch (error) {
    logError(
      {
        error
      },
      'getBuildTenDayDetails failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const updateBuildTenDayLockoutDate = async (
  buildId: number | string,
  options: IBuildTenDayLockoutDatePut
) => {
  try {
    const result = await axios.put(`/api/builds/${buildId}/lockout-date`, options);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          ...options
        }
      },
      'updateBuildTenDayLockoutDate failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getBuildOptionsCategories = async (id: string): Promise<ISpecCategory[]> => {
  try {
    const response = await axios.get(`/api/builds/${id}/option/categories`);
    return response.data;
  } catch (error) {
    logError(error, 'getBuildOptionsCategories failed');
    return Promise.reject(error);
  }
};

export const getBuildOptionCategory = async (
  id: string,
  filters?: {
    category: string;
    isTenDayCategory: boolean;
  }
): Promise<ICategory> => {
  try {
    const response = await axios.get(`/api/builds/${id}/categories`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return response.data;
  } catch (error) {
    logError(error, 'getBuildOptionCategory failed');
    return Promise.reject(error);
  }
};

export const getBrickJointOptions = async (
  id: number,
  filters?: {
    exteriorColorSchemeCode: string;
  }
): Promise<IColorSchemeOption> => {
  try {
    const response = await axios.get(
      `/api/builds/${id}/categories/color-scheme-options/brick-joint`,
      {
        params: filters,
        paramsSerializer: params => qs.stringify(params)
      }
    );
    return response.data;
  } catch (error) {
    logError(error, 'getBrickJointOptions failed');
    return Promise.reject(error);
  }
};

export const getStoneColorOptions = async (
  id: number,
  filters?: {
    stoneTypeOptionId: string;
  }
): Promise<IColorSchemeOption> => {
  try {
    const response = await axios.get(
      `/api/builds/${id}/categories/color-scheme-options/stone-colors`,
      {
        params: filters,
        paramsSerializer: params => qs.stringify(params)
      }
    );
    return response.data;
  } catch (error) {
    logError(error, 'getStoneColorOptions failed');
    return Promise.reject(error);
  }
};

export const getShutterColorOptions = async (
  id: number,
  filters?: {
    exteriorColorSchemeCode: string;
    hasCedarUpgrade: boolean;
  }
): Promise<IColorSchemeOption> => {
  try {
    const response = await axios.get(
      `/api/builds/${id}/categories/color-scheme-options/shutter-colors`,
      {
        params: filters,
        paramsSerializer: params => qs.stringify(params)
      }
    );
    return response.data;
  } catch (error) {
    logError(error, 'getShutterColorOptions failed');
    return Promise.reject(error);
  }
};

export const getSidingColorOptions = async (
  id: number,
  filters?: {
    exteriorColorSchemeCode: string;
  }
): Promise<IColorSchemeOption> => {
  try {
    const response = await axios.get(
      `/api/builds/${id}/categories/color-scheme-options/siding-colors`,
      {
        params: filters,
        paramsSerializer: params => qs.stringify(params)
      }
    );
    return response.data;
  } catch (error) {
    logError(error, 'getSidingColorOptions failed');
    return Promise.reject(error);
  }
};

export const saveBuildContingency = async (id: string, post: IBuildContingencyPost) => {
  try {
    const response = await axios.post(`/api/builds/${id}/contingency`, post);
    return response.data;
  } catch (error) {
    logError({ error, payload: { id, post } }, 'saveBuildContingency failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updateBuild = async (id: string, options: IUpdateBuild): Promise<any> => {
  try {
    const response = await axios.put(`/api/builds/${id}`, options);
    return response.data;
  } catch (error) {
    logError({ error, payload: { id, options } }, 'updateBuild failed');
    return Promise.reject(error);
  }
};

// Removed return type Promise<ISpecOptionDetail> for ease of error handling
export const updateBuildOptions = async (id: string, options: IUpdateOptions): Promise<any> => {
  try {
    const response = await axios.put(`/api/builds/${id}/options`, options);
    return response.data;
  } catch (error) {
    logError({ error, payload: { id, options } }, 'updateBuildOptions failed');
    return Promise.reject(error);
  }
};

export const approveBuild = async (id: string): Promise<any> => {
  try {
    const response = await axios.put(`/api/builds/${id}/approve`);
    return response.data;
  } catch (error) {
    logError({ error, payload: { id } }, 'approveBuild failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getAddendumG = async (buildId: string | number): Promise<AxiosResponse> => {
  try {
    const data = await axios.get(`/api/builds/${buildId}/addendum-g`, {
      responseType: 'blob'
    });
    return data;
  } catch (error: any) {
    logError(error, 'getAddendumG failed');
    return Promise.reject(error.response.data);
  }
};

export const getFinalPriceContract = async (
  buildId: string | number,
  pages?: string | number,
  buildDocumentVersionId?: string | number
): Promise<AxiosResponse> => {
  try {
    const data = await axios.get(`/api/builds/${buildId}/final-price-contract`, {
      responseType: 'blob',
      params: { buildDocumentVersionId, pages }
    });
    return data;
  } catch (error: any) {
    logError(error, 'getFinalPriceContract failed');
    return Promise.reject(error.response.data);
  }
};

export const updateContractAdjustment = async (
  buildId: number | string,
  options: IContractAdjustment
) => {
  try {
    const result = await axios.put(`/api/builds/${buildId}/contractadjustment`, options);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          ...options
        }
      },
      'updateContractAdjustment failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getPersonBuilds = async (
  personId: number | string,
  filters?: {
    sortBy?: string;
    sortDirection?: string;
    page?: number;
    perPage?: number | string;
    buildStatuses?: null | BuildStatusValue[];
    buildHasSitePlan?: boolean;
  }
): Promise<IResponse<IBuild[]>> => {
  try {
    const { data } = await axios.get(`/api/clients/${personId}/builds`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getPersonBuilds failed');
    return Promise.reject(error);
  }
};

export const getBuildCeremonies = async (filters?: {
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number | string;
}): Promise<IResponse<IBuildCeremonies[]>> => {
  try {
    const { data } = await axios.get(`/api/build-ceremonies`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuildCeremonies failed');
    return Promise.reject(error);
  }
};

export const softDeleteBuild = async (buildId: number | string) => {
  try {
    const result = await axios.put(`/api/builds/soft-delete/${buildId}`);
    return result.data;
  } catch (error) {
    logError(
      {
        error
      },
      'softDeleteBuild failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const restoreBuild = async (buildId: number | string) => {
  try {
    const result = await axios.put(`/api/builds/restore/${buildId}`);
    return result.data;
  } catch (error) {
    logError(
      {
        error
      },
      'restoreBuild failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getAlertStatuses = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get('/api/builds/alert-statuses');
    return data;
  } catch (error) {
    logError(
      {
        error
      },
      'getAlertStatuses failed'
    );
    return Promise.reject(error);
  }
};

export const saveLenderInfo = async (buildId: string | number, payload: ILenderPayload) => {
  try {
    const response = await axios.post(`/api/sales/contracts/lender-info/${buildId}`, payload);
    return response.data;
  } catch (error) {
    logError({ error, payload: { payload } }, 'saveLenderInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const doesContractExist = async (
  buildId: number | string
): Promise<{ hasContract: boolean }> => {
  try {
    const { data } = await axios.get(`/api/builds/${buildId}/contract-exists`);
    return data;
  } catch (error) {
    logError(
      {
        error
      },
      'doesContractExist failed'
    );
    return Promise.reject(error);
  }
};

export const getBuildNotes = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`/api/builds/${buildId}/notes`);
    return data;
  } catch (error: any) {
    logError(error, 'getContractDetails failed');
    return Promise.reject(error.response.data);
  }
};

export const postBuildNotes = async (buildId: number | string, notes: string): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/builds/${buildId}/notes`, { notes });
    return data;
  } catch (error: any) {
    logError(error, 'postBuildNotes failed');
    return Promise.reject(error.response.data);
  }
};

export const downloadSitePlan = async (buildId: number | string): Promise<any> => {
  try {
    await axios.get(`/api/builds/${buildId}/site-plan`, { responseType: 'blob' }).then(result => {
      const parsedContentDisposition = contentDisposition.parse(
        result.headers['content-disposition']
      );
      const { filename: fileName } = parsedContentDisposition.parameters;
      fileDownload(result.data, fileName);
    });
  } catch (error: any) {
    logError(error, 'downloadSitePlan failed');
    return Promise.reject(error.response?.data);
  }
};

export const downloadContract = async (buildId: number | string): Promise<any> => {
  try {
    await axios.get(`/api/builds/${buildId}/contract`, { responseType: 'blob' }).then(result => {
      const parsedContentDisposition = contentDisposition.parse(
        result.headers['content-disposition']
      );
      const { filename: fileName } = parsedContentDisposition.parameters;
      fileDownload(result.data, fileName);
    });
  } catch (error: any) {
    logError(error, 'downloadContract failed');
    return Promise.reject(error.response?.data);
  }
};

export const submitWarrantyRequest = async (buildId: number | string, data: any) => {
  try {
    const response = await axios.post(`/api/builds/${buildId}/warranty-ask`, data);
    return response.data;
  } catch (error) {
    return Promise.reject(error?.response?.data);
  }
};